import L from 'leaflet'
import type { ColorPair } from '@/features/subgeo/utils/color-utils'
import { addBasemap } from '@/helpers/basemap'
import type { ViewOnlySubGeo } from '../types'

export interface GeoJSONFeatureProperties {
	id: string
	name: string
}

const isValidCoordinate = (coord: unknown): coord is [number, number] => {
	return (
		Array.isArray(coord) &&
		coord.length >= 2 &&
		typeof coord[0] === 'number' &&
		typeof coord[1] === 'number'
	)
}

export const validateGeometry = (geom: unknown): boolean => {
	if (!geom || typeof geom !== 'object') {
		return false
	}
	const g = geom as { type: string; coordinates: unknown }
	if (!Array.isArray(g.coordinates)) {
		return false
	}

	if (g.type === 'Polygon') {
		const c = g.coordinates
		return (
			Array.isArray(c) &&
			Array.isArray(c[0]) &&
			c[0].length > 0 &&
			isValidCoordinate(c[0][0])
		)
	}
	if (g.type === 'MultiPolygon') {
		const c = g.coordinates
		return (
			Array.isArray(c) &&
			Array.isArray(c[0]) &&
			Array.isArray(c[0][0]) &&
			c[0][0].length > 0 &&
			isValidCoordinate(c[0][0][0])
		)
	}
	return false
}

export const filterValidSubgeos = (
	subgeos: ViewOnlySubGeo[]
): ViewOnlySubGeo[] => {
	return subgeos.filter((s) => validateGeometry(s?.geometry))
}

const calculateRingArea = (coords: number[][]): number => {
	let area = 0
	const len = coords.length

	for (let i = 0; i < len - 1; i++) {
		const [x1, y1] = coords[i]
		const [x2, y2] = coords[i + 1]
		area += x1 * y2 - x2 * y1
	}

	return area / 2
}

const calculatePolygonArea = (geometry: GeoJSON.Geometry): number => {
	if (geometry.type === 'Polygon') {
		return Math.abs(calculateRingArea(geometry.coordinates[0]))
	}
	if (geometry.type === 'MultiPolygon') {
		return geometry.coordinates.reduce((total, polygon) => {
			return total + Math.abs(calculateRingArea(polygon[0]))
		}, 0)
	}
	return 0
}

export const buildGeoJSONFeatureCollection = (
	subgeos: ViewOnlySubGeo[]
): GeoJSON.FeatureCollection<
	GeoJSON.Geometry,
	GeoJSONFeatureProperties
> | null => {
	if (subgeos.length === 0) {
		return null
	}

	const subgeosWithArea = subgeos.map((s) => ({
		subgeo: s,
		area: calculatePolygonArea(s.geometry)
	}))

	subgeosWithArea.sort((a, b) => b.area - a.area)

	const features = subgeosWithArea.map(({ subgeo }) => ({
		type: 'Feature' as const,
		properties: {
			id: subgeo.id,
			name: subgeo.name
		},
		geometry: subgeo.geometry
	}))

	return {
		type: 'FeatureCollection',
		features
	}
}

export const createStyleFunction = (getColor: (id: string) => ColorPair) => {
	return (feature: GeoJSON.Feature | undefined): L.PathOptions => {
		const id = feature?.properties?.id as string
		if (!id) {
			return {}
		}

		const colors = getColor(id)
		return {
			fillColor: colors.fill,
			fillOpacity: colors.fillOpacity,
			color: colors.border,
			weight: 1
		}
	}
}

let tooltipDebounceTimer: ReturnType<typeof setTimeout> | null = null
let currentTooltipLayer: (L.Path & { feature?: GeoJSON.Feature }) | null = null
const TOOLTIP_DELAY = 150

export const attachHoverHandlers = (
	layer: L.GeoJSON,
	createTooltip: (name: string) => HTMLDivElement
): void => {
	layer.on('mouseover', (e: L.LeafletMouseEvent) => {
		const target = e.layer as L.Path & { feature?: GeoJSON.Feature }
		const name = target.feature?.properties?.name as string

		if (!name) {
			return
		}

		if (currentTooltipLayer && currentTooltipLayer !== target) {
			currentTooltipLayer.closeTooltip()
			currentTooltipLayer.unbindTooltip()
			currentTooltipLayer = null
		}

		const element = target.getElement()
		if (element) {
			;(element as HTMLElement).style.cursor = 'pointer'
		}

		if (tooltipDebounceTimer) {
			clearTimeout(tooltipDebounceTimer)
		}

		tooltipDebounceTimer = setTimeout(() => {
			const tooltipElement = createTooltip(name)
			const tooltip = L.tooltip({
				permanent: false,
				direction: 'top',
				className: 'geometry-check-tooltip',
				opacity: 1
			})
			tooltip.setContent(tooltipElement)
			target.bindTooltip(tooltip)
			target.openTooltip()
			currentTooltipLayer = target
		}, TOOLTIP_DELAY)
	})

	layer.on('mouseout', (e: L.LeafletMouseEvent) => {
		if (tooltipDebounceTimer) {
			clearTimeout(tooltipDebounceTimer)
			tooltipDebounceTimer = null
		}

		const target = e.layer as L.Path
		const element = target.getElement()
		if (element) {
			;(element as HTMLElement).style.cursor = ''
		}
	})
}

export const createGeoJSONLayer = (
	featureCollection: GeoJSON.FeatureCollection,
	styleFunction: (feature: GeoJSON.Feature | undefined) => L.PathOptions,
	createTooltip: (name: string) => HTMLDivElement
): L.GeoJSON => {
	const layer = L.geoJSON(featureCollection, {
		style: styleFunction
	})

	attachHoverHandlers(layer, createTooltip)

	return layer
}

export const fitMapToBounds = (
	map: L.Map,
	layer: L.GeoJSON,
	options?: {
		paddingTopLeft?: [number, number]
		paddingBottomRight?: [number, number]
		maxZoom?: number
		animate?: boolean
		duration?: number
	}
): void => {
	const bounds = layer.getBounds()
	if (!bounds.isValid()) {
		return
	}

	requestAnimationFrame(() => {
		map.fitBounds(bounds, {
			paddingTopLeft: options?.paddingTopLeft ?? [450, 20],
			paddingBottomRight: options?.paddingBottomRight ?? [20, 20],
			maxZoom: options?.maxZoom ?? 12,
			animate: options?.animate ?? true,
			duration: options?.duration ?? 1.5
		})
	})
}

export const initializeLeafletMap = (
	container: HTMLDivElement,
	options?: {
		center?: [number, number]
		zoom?: number
		minZoom?: number
		canvasTolerance?: number
		canvasPadding?: number
	}
): L.Map => {
	const map = L.map(container, {
		center: options?.center ?? [20, 0],
		zoom: options?.zoom ?? 3,
		minZoom: options?.minZoom ?? 3,
		scrollWheelZoom: true,
		preferCanvas: true,
		zoomControl: false,
		renderer: L.canvas({
			tolerance: options?.canvasTolerance ?? 5,
			padding: options?.canvasPadding ?? 0.5
		})
	})

	addBasemap(map)

	L.control
		.zoom({
			position: 'bottomright'
		})
		.addTo(map)

	return map
}
